/**
 * Centralized Form Validation Utility
 * Enforces Indian mobile patterns, RFC email specs, password strength, and file upload limits.
 * All validator functions provide both `.error` and `.message` properties for 100% interoperability.
 */

export const PHONE_REGEX = /^[6-9]\d{9}$/;
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Validates a 10-digit Indian phone number (optionally cleans +91 or leading 0)
 */
export function validatePhone(phone) {
  if (!phone || !String(phone).trim()) {
    const msg = 'Mobile number is required.';
    return { valid: false, error: msg, message: msg };
  }
  
  // Clean non-digits
  let digits = String(phone).replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) {
    digits = digits.slice(2);
  } else if (digits.length === 11 && digits.startsWith('0')) {
    digits = digits.slice(1);
  }

  if (digits.length !== 10) {
    const msg = 'Mobile number must be exactly 10 digits.';
    return { valid: false, error: msg, message: msg };
  }

  if (!PHONE_REGEX.test(digits)) {
    const msg = 'Please enter a valid Indian mobile number starting with 6, 7, 8, or 9.';
    return { valid: false, error: msg, message: msg };
  }

  return { valid: true, error: null, message: null, cleaned: digits };
}

/**
 * Validates email format
 */
export function validateEmail(email) {
  if (!email || !String(email).trim()) {
    const msg = 'Email address is required.';
    return { valid: false, error: msg, message: msg };
  }
  if (!EMAIL_REGEX.test(String(email).trim())) {
    const msg = 'Please enter a valid email address (e.g. name@domain.com).';
    return { valid: false, error: msg, message: msg };
  }
  return { valid: true, error: null, message: null, cleaned: String(email).trim().toLowerCase() };
}

/**
 * Validates password strength (min 8 characters, at least 1 digit, 1 letter)
 */
export function validatePassword(password) {
  if (!password) {
    const msg = 'Password is required.';
    return { valid: false, error: msg, message: msg };
  }
  if (password.length < 8) {
    const msg = 'Password must be at least 8 characters long.';
    return { valid: false, error: msg, message: msg };
  }
  if (!/\d/.test(password)) {
    const msg = 'Password must contain at least one number.';
    return { valid: false, error: msg, message: msg };
  }
  if (!/[a-zA-Z]/.test(password)) {
    const msg = 'Password must contain at least one letter.';
    return { valid: false, error: msg, message: msg };
  }
  return { valid: true, error: null, message: null };
}

/**
 * Validates file upload size and MIME type
 */
export function validateFileUpload(file, maxMb = 5, allowedTypes = ['image/jpeg', 'image/png', 'image/webp']) {
  if (!file) return { valid: true, error: null, message: null };

  const maxBytes = maxMb * 1024 * 1024;
  if (file.size > maxBytes) {
    const msg = `File "${file.name}" exceeds the maximum allowed size of ${maxMb}MB.`;
    return { valid: false, error: msg, message: msg };
  }

  const fileType = (file.type || '').toLowerCase();
  if (fileType && !allowedTypes.includes(fileType)) {
    const msg = `File type "${file.type || 'unknown'}" is not supported. Please upload JPG, PNG, or WEBP.`;
    return { valid: false, error: msg, message: msg };
  }

  return { valid: true, error: null, message: null };
}

/**
 * Validates public service enquiry / lead form
 */
export function validateLeadForm({ name, phone, serviceType, service_type, service_label, location, locality, locality_label, file }) {
  const errors = {};

  if (!name || String(name).trim().length < 2) {
    errors.name = 'Full name must be at least 2 characters long.';
  }

  const phoneRes = validatePhone(phone);
  if (!phoneRes.valid) {
    errors.phone = phoneRes.error;
  }

  const service = serviceType || service_type || service_label;
  if (!service || !String(service).trim()) {
    errors.service = 'Please select a shutter service.';
  }

  const area = location || locality || locality_label;
  if (!area || !String(area).trim()) {
    errors.locality = 'Please select your Hyderabad location.';
  }

  if (file) {
    const fileRes = validateFileUpload(file, 5);
    if (!fileRes.valid) {
      errors.file = fileRes.error;
    }
  }

  const errorKeys = Object.keys(errors);
  const firstError = errorKeys.length > 0 ? errors[errorKeys[0]] : null;

  return {
    valid: errorKeys.length === 0,
    errors,
    firstError,
    cleanedPhone: phoneRes.cleaned || phone
  };
}
